<template>
  <div :key="showPage ? 'show' : 'hide'">
    <template v-if="isInitialized">
      <router-view v-if="showPage" />
      <dialog-router-view :auth="isAuthorized" />
      <!-- $route.name/.matched aren't populated until the router's initial
           navigation resolves (it awaits the matched route's async page
           component). Wait for that so LogIn doesn't briefly render without
           tabs (customerID falls back to null) before flipping once ready. -->
      <template v-if="routerReady">
        <LogIn v-if="!user && !showPage" :value="true" />
        <!-- Logged in, but not authorized for this route (e.g. a customer
             opening a quote link billed to a different contact). Without this
             the page renders nothing at all — mirrors DialogRouterView. -->
        <LogIn
          v-else-if="user && !showPage"
          :value="true"
          :title="$t('error.accessDenied')"
          color="admin"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import ICONS from "./icons";
import { forEach } from "lodash";

export default {
  name: "App",
  components: {
    LogIn: defineAsyncComponent(() => import("./dialogs/LogIn")),
  },
  data() {
    return {
      routerReady: false,
    };
  },
  computed: {
    isInitialized() {
      return this.$store.state.main.initialized;
    },
    isAuthorized() {
      if (this.$route.name === "home" && this.$store.getters.isCustomer()) {
        this.$router.replace({
          name: "customer-home",
          params: { customerID: this.$store.state.main.user.id },
        });
        return true;
      }
      return this.$route.matched.every(
        (route) => !route.meta.auth || route.meta.auth(this.$route.params)
      );
    },
    user() {
      return this.$store.state.main.user;
    },
    showPage() {
      return (
        (this.user && this.isAuthorized) ||
        ["auth", "customer-feedback", "sample-public"].includes(
          this.$route.name
        )
      );
    },
  },
  watch: {
    isInitialized(init) {
      if (init && !this.isAuthorized) {
        this.$q.loading.hide();
      }
    },
    showPage(newVal) {
      // Force re-render when showPage changes (Vue 3 reactivity fix)
      if (newVal) {
        this.$forceUpdate();
      }
    },
    user(newVal) {
      // Force re-render when user changes (Vue 3 reactivity fix)
      if (newVal) {
        this.$forceUpdate();
      }
    },
  },
  async beforeCreate() {
    this.$q.dark.set(this.$store.state.main.dark);

    this.$q.iconMapFn = (name) => {
      const icon = ICONS[name];
      if (icon !== undefined) {
        return { icon };
      }
    };

    try {
      this.$q.loading.show();
      await this.$store.dispatch("INIT");
    } catch (error) {
      this.$q.loading.hide();
      this.$store.dispatch("NOTIFY_ERROR", error);
    }
  },
  beforeUnmount() {
    forEach(this.$store.state.main.listeners, (listener) =>
      this.$store.dispatch("UNLISTEN", listener)
    );
  },
  async created() {
    await this.$router.isReady();
    this.routerReady = true;
  },
  mounted() {
    if (import.meta.env.DEV && import.meta.env.MODE === "development") {
      window.app = this;
    }
  },
};
</script>
